/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useMemo} from 'react'
import {topToaster, bottomToaster, toaster, Toaster} from '../components/toaster'

export {topToaster, bottomToaster, toaster, Toaster}

/**
 * Display a toast message on the screen conforming to Xleap Design System.
 *
 * @param {object} defaultOptions Default options applied to all toasts from this hook instance
 * @returns {function} toast function to display a toast
 */
export function useToast(defaultOptions = {}) {
    return useMemo(() => {
        const toastFn = (options = {}) => {
            const merged = {...defaultOptions, ...options}
            const {
                title,
                description,
                icon,
                id,
                status,
                action,
                placement = 'bottom-end',
                duration = 20000,
                isClosable = true,
                onClose
            } = merged

            const toastType = status || merged.type || 'default'
            const toastId = id || `toast:${Math.random().toString(36).slice(2, 9)}`
            const resolvedDuration = duration === null ? Infinity : duration
            const toastPayload = {
                id: toastId,
                title,
                description,
                type: toastType,
                icon,
                action,
                isClosable,
                duration: resolvedDuration,
                onClose
            }

            const targetToaster = placement === 'top-end' ? topToaster : bottomToaster
            // Microtask deferral lets React commit context (e.g. useEffect) finish so consecutive create() calls are not overwritten
            queueMicrotask(() => targetToaster.create(toastPayload))

            return toastId
        }

        toastFn.close = (id) => {
            if (id) {
                topToaster.dismiss(id)
                bottomToaster.dismiss(id)
            } else {
                topToaster.dismiss()
                bottomToaster.dismiss()
            }
        }

        return toastFn
    }, [defaultOptions])
}
